import { calculateOrderCost } from '../../helpers/helpers';

/* ------------------------------------------------------------------ */
/* Ride tiers — the three backend vehicles presented Yango-style       */
/* ------------------------------------------------------------------ */

export const RIDE_TIERS = [
  {
    id: 'Suzuki',
    vehicle: 'Suzuki',
    name: 'MBZ Mini',
    tagline: 'Quick city trips · small loads',
    capacity: 'Up to 3 bags',
    icon: 'bi-truck-front',
  },
  {
    id: 'Mazda',
    vehicle: 'Mazda',
    name: 'MBZ Van',
    tagline: 'Bigger loads · home shifting',
    capacity: 'Up to 8 bags',
    icon: 'bi-truck',
  },
  {
    id: 'Shehzore',
    vehicle: 'Shehzore',
    name: 'MBZ XL',
    tagline: 'Bulky cargo · furniture',
    capacity: 'Full load',
    icon: 'bi-truck-flatbed',
  },
];

export const DEFAULT_TIER = 'Suzuki';

export const getTier = (id) => RIDE_TIERS.find((t) => t.id === id) || RIDE_TIERS[0];

/* ------------------------------------------------------------------ */
/* Fares                                                               */
/* ------------------------------------------------------------------ */

/** "12.4 km" -> 12.4 ; anything unparseable -> 0 */
export const parseDistanceKm = (distanceText) => {
  if (!distanceText) return 0;
  const n = parseFloat(String(distanceText).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export const tierFare = (distanceKm, vehicle) => {
  try {
    return calculateOrderCost(distanceKm, vehicle).totalAmount;
  } catch {
    return '0.00';
  }
};

export const formatPKR = (amount) =>
  `PKR ${Number(amount || 0).toLocaleString('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/* ------------------------------------------------------------------ */
/* Order lifecycle (mirrors backend orderStatus enum)                  */
/* ------------------------------------------------------------------ */

export const STATUS = {
  FINDING: 'Finding Driver',
  NOT_DISPATCHED: 'Vehicle not dispatched yet',
  DISPATCHED: 'Vehicle Dispatched',
  ARRIVED_PICKUP: 'Vehicle Arrived At pickup location',
  LEFT_PICKUP: 'Vehicle has left from pick up location',
  ARRIVED_DEST: 'Vehicle Arrived At Destination location',
  COMPLETED: 'Job Completed Successfully',
  CANCELED: 'Booking Canceled',
};

/** The six in-flow steps shown as progress dots. */
export const STATUS_FLOW = [
  STATUS.FINDING,
  STATUS.NOT_DISPATCHED,
  STATUS.DISPATCHED,
  STATUS.ARRIVED_PICKUP,
  STATUS.LEFT_PICKUP,
  STATUS.ARRIVED_DEST,
];

export function statusMeta(orderStatus) {
  switch (orderStatus) {
    case STATUS.FINDING:
      return { tone: 'searching', title: 'Searching for your driver', sub: 'Connecting you with nearby MBZ drivers…' };
    case STATUS.NOT_DISPATCHED:
      return { tone: 'waiting', title: 'Driver assigned', sub: 'Your driver is getting ready to head out' };
    case STATUS.DISPATCHED:
      return { tone: 'onway', title: 'Driver on the way', sub: 'Heading to your pickup point' };
    case STATUS.ARRIVED_PICKUP:
      return { tone: 'arrived', title: 'Your driver has arrived', sub: 'Meet them at the pickup spot' };
    case STATUS.LEFT_PICKUP:
      return { tone: 'ontrip', title: 'On the way', sub: 'Enjoy your ride with MBZ' };
    case STATUS.ARRIVED_DEST:
      return { tone: 'done', title: 'You have arrived', sub: 'Confirm below to finish your trip' };
    case STATUS.COMPLETED:
      return { tone: 'complete', title: 'Trip complete', sub: 'Thanks for riding with MBZ!' };
    case STATUS.CANCELED:
      return { tone: 'canceled', title: 'Booking canceled', sub: 'This trip was canceled' };
    default:
      return { tone: 'searching', title: 'Loading trip status…', sub: '' };
  }
}

/** 1-based position in the progress flow; 0 before dispatch, -1 when canceled. */
export function statusStep(orderStatus) {
  if (orderStatus === STATUS.CANCELED) return -1;
  if (orderStatus === STATUS.COMPLETED) return STATUS_FLOW.length;
  const i = STATUS_FLOW.indexOf(orderStatus);
  return i === -1 ? 0 : i + 1;
}

export const canCancel = (orderStatus) =>
  orderStatus === STATUS.FINDING || orderStatus === STATUS.NOT_DISPATCHED;

export const canFinish = (orderStatus, userRecieved) =>
  orderStatus === STATUS.ARRIVED_DEST && !userRecieved;

export const isTerminal = (orderStatus) =>
  orderStatus === STATUS.COMPLETED || orderStatus === STATUS.CANCELED;

/* ------------------------------------------------------------------ */
/* Google Places → normalized endpoint field                           */
/* ------------------------------------------------------------------ */

/** Turn a google.maps.places.PlaceResult (or mock) into a plain object. */
export function placeFromGoogle(place) {
  const lat = place?.geometry?.location?.lat?.();
  const lng = place?.geometry?.location?.lng?.();
  const label = place?.formatted_address || place?.name || '';
  const short = place?.name || (label ? label.split(',')[0] : '');
  const comps = place?.address_components || [];
  const pick = (type) => comps.find((c) => (c.types || []).includes(type))?.long_name;
  const city = pick('locality') || pick('administrative_area_level_2') || pick('administrative_area_level_1') || 'Karachi';
  return { label, short, lat, lng, city };
}

/* ------------------------------------------------------------------ */
/* Backend order payload (exact shape /orders/new expects)             */
/* ------------------------------------------------------------------ */

export function buildRideOrder({ pickup, dropoff, distanceText, tierId, notes = '' }) {
  const tier = getTier(tierId);
  const km = parseDistanceKm(distanceText);
  const totalAmount = tierFare(km, tier.vehicle);

  return {
    orderItems: [],
    userAddressInfo: {
      adminProposedAmount: '',
      notes,
      vehicle: tier.vehicle,
      loadType: 'Full Truck',
      bookingType: 'Local',
      startAddress: pickup.label,
      destinationAddress: dropoff.label,
      startCity: pickup.city,
      destinationCity: dropoff.city,
    },
    googleMap: {
      origin: pickup.label,
      originLatitude: pickup.lat ?? undefined,
      originLongitude: pickup.lng ?? undefined,
      destination: dropoff.label,
      distance: distanceText,
    },
    goodsInfo: {
      goodsType: 'Other',
      totalWeight: 0,
      loadingType: 'Labour',
      volume: 0,
      userProposedAmount: '',
    },
    labour: {
      requiredLaboursStart: false,
      noOfLaboursStart: 0,
      buildingFloorsStart: 0,
      cargoLiftStart: false,
      requiredLaboursDestination: false,
      noOfLaboursDestination: 0,
      buildingFloorsDestination: 0,
      cargoLiftDestination: false,
    },
    totalAmount,
    paymentMethod: 'COD',
  };
}
